/**
 * The app chrome around screens (ui-ux §3, §11): the base and sheet layers inside `#ui`, and F3's arcade tab bar
 * (`tabBar`, ui-ux §19.3; it becomes the left rail on landscape phones, tablets and desktop) — «Житие» · «Странствия» ·
 * Ристалище (the raised wax seal) · «Таверна» · «Орден». Immersive, stage and debug screens hide it (ui-ux §3.1 rule 5).
 *
 * Tab behaviour (ui-ux §3.4): switching restores the tab's last route; tapping the active tab pops to its root, and at
 * the root scrolls to the top. `1`–`5` switch tabs outside immersive screens (ui-ux §7.5; wired in main.ts).
 */
import { signal, type TabId, type TFunction } from '../shared';
import { artImage, tabBar, type TabSpec } from '../ui';
import type { Mounted } from './router';

export const TABS: ReadonlyArray<{ tab: TabId; hash: string; key: string; emblem: 'book' | 'map' | 'swords' | 'tankard' | 'banner' }> = [
  { tab: 'life', hash: '#/life', key: 'ui.tab.life', emblem: 'book' },
  { tab: 'road', hash: '#/road', key: 'ui.tab.road', emblem: 'map' },
  { tab: 'arena', hash: '#/arena', key: 'ui.tab.arena', emblem: 'swords' },
  { tab: 'cards', hash: '#/cards', key: 'ui.tab.cards', emblem: 'tankard' },
  { tab: 'order', hash: '#/order', key: 'ui.tab.order', emblem: 'banner' },
];

/** Screen kinds that show the tab bar. */
const CHROME_KINDS = new Set(['tabRoot', 'page', 'sheet', 'sheetPage', 'overlay']);

export interface Shell {
  base: HTMLElement;
  sheet: HTMLElement;
  /** Updates the tab bar (visibility, current tab) for the new top of the stack. */
  update(top: Mounted, base: Mounted): void;
  /** Tab memory: the last route seen in each tab. */
  remember(tab: TabId, hash: string): void;
  /** Where a tab activation goes: a hash, or «scroll to top» when already at that tab's root. */
  tabTarget(tab: TabId, currentHash: string | null, currentTab: TabId | undefined): { hash: string } | { scrollTop: true };
}

export function createShell(ui: HTMLElement, t: TFunction, onTab: (tab: TabId) => void): Shell {
  ui.replaceChildren();
  const base = document.createElement('div');
  base.className = 'mc-layer-base';
  const sheet = document.createElement('div');
  sheet.className = 'mc-layer-sheet';
  const current = signal<TabId | null>(null);
  const tabs: TabSpec[] = TABS.map((x) => ({
    id: x.tab,
    label: t(x.key),
    compactLabel: t(`ui.tab.short.${x.tab}`),
    href: x.hash,
    icon: artImage(`icons/shell/tab_${x.tab === 'cards' ? 'tavern' : x.tab}`, { size: x.tab === 'arena' ? 36 : 28, emblem: x.emblem, tone: 'night' }),
  }));
  const nav = tabBar({ tabs, current, label: t('ui.nav.label'), onSelect: (id) => onTab(id) });
  ui.append(base, nav, sheet);
  const setChrome = (on: boolean): void => {
    ui.dataset.chrome = on ? 'on' : 'off';
    document.documentElement.dataset.chrome = ui.dataset.chrome;
    nav.toggleAttribute('inert', !on);
  };
  setChrome(false);
  const memory = new Map<TabId, string>();

  return {
    base,
    sheet,
    update(top, b) {
      setChrome(CHROME_KINDS.has(b.def.kind ?? ''));
      ui.dataset.screen = top.def.id;
      current.set(top.def.tab ?? b.def.tab ?? null);
    },
    remember(tab, hash) { memory.set(tab, hash); },
    tabTarget(tab, currentHash, currentTab) {
      const root = TABS.find((x) => x.tab === tab)!.hash;
      if (currentTab === tab) {
        if (currentHash && currentHash.split('?')[0] !== root) return { hash: root };
        return { scrollTop: true };
      }
      return { hash: memory.get(tab) ?? root };
    },
  };
}
